import type { ServerSideConfigManager } from './use-server-side-config';
import type { SetupStep } from './setup-steps';
import { CheckCircle2, Circle, CircleDashed } from 'lucide-react';
import { cn } from 'helpers/cn';
import { COPY } from './copy';
import { GuideLink } from './GuideLink';
import { CODE_STEP_GUIDE } from './guides';

type SetupChecklistProps = { manager: ServerSideConfigManager };

const STEP_ICON: Record<SetupStep['state'], typeof Circle> = {
  done: CheckCircle2,
  todo: Circle,
  unknown: CircleDashed,
};

/**
 * What a server-side experiment needs before it can start, in order, each step ticked from
 * what the dashboard already knows (the visitor ID flag, the website's keys, the flag and
 * variant keys). The code step and the notes have no tick: only the customer can tell.
 */
export const SetupChecklist = ({ manager }: SetupChecklistProps) => {
  const { setupSteps, conversionsNote } = manager;

  return (
    <div className="flex flex-col gap-2">
      <span className="micro-label">{COPY.setup.title}</span>
      <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
        {setupSteps.map((entry) => {
          const Icon = STEP_ICON[entry.state];
          return (
            <li key={entry.id} className="flex items-start gap-2 text-meta">
              <Icon
                aria-label={entry.state === 'done' ? 'Done' : entry.state === 'todo' ? 'To do' : 'Not known'}
                className={cn('mt-0.5 size-4 shrink-0', entry.state === 'done' ? 'text-ok' : 'text-dim')}
                strokeWidth={1.75}
              />
              <span className={entry.state === 'done' ? 'text-dim' : 'text-foreground'}>
                {entry.text}
                {entry.state !== 'done' && (
                  <>
                    {' '}
                    <GuideLink guide={entry.guide} label={COPY.guides.how} />
                  </>
                )}
              </span>
            </li>
          );
        })}
        <li className="flex items-start gap-2 text-meta">
          <Circle aria-hidden="true" className="text-dim mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
          <span className="text-foreground">
            {COPY.setup.code} <GuideLink guide={CODE_STEP_GUIDE} label={COPY.guides.how} />
          </span>
        </li>
      </ol>
      <p className="supporting m-0">{COPY.setup.visitorHeader}</p>
      <p className="supporting m-0">{conversionsNote}</p>
    </div>
  );
};
